import {Autocomplete, Box, Button, Grid, InputAdornment, Stack, TextField, Typography} from "@mui/material";
import React from "react";
import {observer} from "mobx-react";
import {blueGrey, grey, indigo} from "@mui/material/colors";
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import {DateRangePicker} from "app/modules/common/date-range-picker/date-range-picker";
import {BoxCenter} from "app/modules/common/layout/components/containers";
import {NavigateHook} from "app/modules/common/layout/hooks/navigate-hook";
import {useInstance} from "react-ioc";
import {SearchStore} from "app/modules/main/components/main/search/SearchStore";
import {SearchCitiesResult} from "app/modules/main/api/main-clients.api";
import {DebounceInput} from "app/modules/main/components/common/debounce-input";
import {LocationRenderOption} from "app/modules/main/components/main/search/location-render";
import BackgroundImage from "app/modules/common/assets/background.jpg";
import useEmblaCarousel from "embla-carousel-react";
import { EmblaOptionsType } from 'embla-carousel'
import {ResultItemCard} from "app/modules/main/components/main/search/result-item";

const options : EmblaOptionsType = {
    align: "center",
    startIndex: 1
};

export default observer(() => {
    const [searchRedirect, setSearchRedirect] = React.useState(false);
    const store = useInstance(SearchStore);

    const [emblaRef] = useEmblaCarousel(options);

    if(searchRedirect) {
        return <NavigateHook path={"/search"}/>;
    }

    const filterOptions = (options: SearchCitiesResult[]): SearchCitiesResult[] => options;

    let location = <Autocomplete
        sx={{backgroundColor: "#fff", borderRadius: 2, minWidth: "250px"}}
        options={store.searchCitiesResult}
        value={store.searchValue}
        onChange={(event: unknown, value: SearchCitiesResult | null) => store.setSearchValue(value, true)}
        getOptionLabel={(item: SearchCitiesResult) => `${item.city}, ${item.state}`}
        renderInput={(params) => <DebounceInput
            placeholder="Type in here…"
            debounceTimeout={400}
            handleDebounce={(value: string) => {
                store.setSearchString(value);
                store.searchCities();
            }}
            {...params}
        />}
        renderOption={LocationRenderOption}
        loading={store.isLoading}
        filterOptions={filterOptions}
        blurOnSelect={true}
    />;

    let dateRange = <Box>
        <DateRangePicker
            disablePast
            value={store.dates}
            onChange={(newValue) => store.setDates(newValue)}
            textFieldSx={{ backgroundColor: "#fff", borderRadius: 2, width: "100%", minWidth: "250px !important" }}
        />
    </Box>;
    let guests = <TextField
        id="outlined-basic" sx={{ backgroundColor: "#fff", borderRadius: 2 }}
        InputProps={{
            startAdornment: (
                <InputAdornment position="start">
                    <PeopleAltIcon />
                </InputAdornment>
            ),
        }}
        value={store.searchParams.guestsAdults}
        onChange={(e) => {
            let value = parseFloat(e.target.value);
            if(isNaN(value) || value < 1) {
                value = 1;
            }

            store.setGuestAdults(value);
        }}
        type={"number"}
        placeholder={'1'}
        variant="outlined"
    />;

    return (<Grid container>
        <Grid item xs={12} sx={{ display: { xs: "block", md: "none" }, backgroundColor: indigo[50], pb: 2, pl: 2, pr: 2 }}>
            <Stack direction={"column"} spacing={2}>
                <Typography align={"center"} variant={"h5"} sx={{ pt: 2, pb: 2 }}>Find a vacation place to relax</Typography>
                {location}
                {dateRange}
                {guests}
                <Button variant="contained" sx={{ width: "100%", height: "56px", backgroundColor: blueGrey[800] }} onClick={() => setSearchRedirect(true)}>Search</Button>
            </Stack>
        </Grid>
        <Grid item xs={12} sx={{ display: { xs: "none", md: "block" }, pt: 12, pb: 12, backgroundImage: 'url("' + BackgroundImage + '")', backgroundRepeat: "no-repeat", backgroundPosition: "center", backgroundSize: "cover", position: "relative" }}>
            <Box sx={{ position: "absolute", inset: 0, backgroundColor: "rgba(0,0,0,0.3)" }} />
            <Grid container sx={{ position: "relative", zIndex: 1 }}>
                <Grid item xs={1} lg={2}></Grid>
                <Grid item xs={10} lg={8}>
                    <BoxCenter>
                        <Stack direction={"column"} sx={{ backdropFilter: "blur(12px)", backgroundColor: "rgba(255,255,255,0.15)", p: 4, pt: 5, pb: 5, borderRadius: 3, border: "1px solid rgba(255,255,255,0.25)" }}>
                            <Typography align={"center"} variant={"h3"} sx={{ pb: 3, color: "#fff", fontWeight: 600, textShadow: "0 2px 4px rgba(0,0,0,0.3)" }}>Find a vacation place to relax</Typography>
                            <Stack direction={"row"} spacing={1.5} alignItems={"center"}>
                                {location}
                                {dateRange}
                                {guests}
                                <Button variant="contained" sx={{ width: "auto", minWidth: "120px", height: "56px", backgroundColor: blueGrey[800], borderRadius: 2, fontWeight: 600, fontSize: "15px", textTransform: "none", "&:hover": { backgroundColor: blueGrey[900] } }} onClick={() => setSearchRedirect(true)}>Search</Button>
                            </Stack>
                        </Stack>
                    </BoxCenter>
                </Grid>
                <Grid item xs={1} lg={2}></Grid>
            </Grid>
        </Grid>
        <Grid item xs={12} sx={{ display: { xs: "none", md: "block" }, pt: 6, pb: 8, backgroundColor: grey[50] }}>
            <Grid container>
                <Grid item xs={1} lg={2}></Grid>
                <Grid item xs={10} lg={8}>
                    <BoxCenter>
                        <Stack direction={"column"} spacing={3} sx={{ width: "100%" }}>
                            <Typography align={"center"} variant={"h4"} sx={{ fontWeight: 600, color: blueGrey[900] }}>Find spaces that suit you</Typography>
                            <Typography align={"center"} sx={{ color: blueGrey[400], fontSize: "16px", mb: 1 }}>Explore our curated selection of vacation rentals</Typography>
                            <Box ref={emblaRef} sx={{ overflow: "hidden", margin: "auto", maxWidth: "85vw", pt: 2, pb: 2 }}>
                                <Box sx={{ display: "flex" }}>
                                    {store.samples?.map(x => <Box sx={{ flex: "0 0 340px", minWidth: 0, pr: 1.5, pl: 1.5 }} key={x.id} ><ResultItemCard item={x} /></Box>)}
                                </Box>
                            </Box>
                        </Stack>
                    </BoxCenter>
                </Grid>
                <Grid item xs={1} lg={2}></Grid>
            </Grid>
        </Grid>
    </Grid>);
})
